import { useContext, useEffect, useRef, useState } from "react";
import { SubscriptionsContext } from "../../../store/SubscriptionsContext";
import { Subscription } from "../../../../../api/subscriptions";
import { SubscriptionTier } from "../../../../../api/tiers";
import {
  TierForm,
  TIER_FORM_ID,
  type TierFormData,
  type TierFormHandle,
} from "../components/TierForm";
import {
  StepNavPanel,
  type StepNavPanelHandle,
} from "../../../modals/StepNavPanel";
import "../../../modals/Modal.css";

export type CreateTierModalBodyProps = {
  activeSub: Subscription;
  onClose: () => void;
};

const FORM_STEP = 0;
const UNSAVED_STEP = 1;
const RESULT_STEP = 2;

export function CreateTierModalBody({
  activeSub,
  onClose,
}: CreateTierModalBodyProps) {
  const { createTier } = useContext(SubscriptionsContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCombo, setIsCombo] = useState(false);
  const [outcome, setOutcome] = useState<"success" | "error" | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const stepNavRef = useRef<StepNavPanelHandle>(null);
  const tierFormRef = useRef<TierFormHandle>(null);

  useEffect(() => {
    if (activeSub.isCombo) {
      setIsCombo(true);
    }
  }, [activeSub]);

  useEffect(() => {
    stepNavRef.current?.goToStep(FORM_STEP);
    setOutcome(null);
    setErrorMessage("");
  }, [activeSub.id]);

  async function handleSubmit(tierData: TierFormData) {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      (await createTier({
        subscriptionId: activeSub.id,
        name: tierData.name,
        price: Number(tierData.price),
        oldCsaMemberPrice: Number(tierData.oldCsaMemberPrice),
        spots: tierData.spots !== undefined ? Number(tierData.spots) : undefined,
        dependsOn: tierData.dependsOn,
        takesFrom: tierData.takesFrom,
      })) as SubscriptionTier;

      setOutcome("success");
      stepNavRef.current?.goToStep(RESULT_STEP);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to create tier.";
      setErrorMessage(msg);
      setOutcome("error");
      stepNavRef.current?.goToStep(RESULT_STEP);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCloseFromResult() {
    setOutcome(null);
    setErrorMessage("");
    stepNavRef.current?.goToStep(FORM_STEP);
    onClose();
  }

  const handleCancel = () => {
    onClose();
  };

  const steps = [
    {
      content: (
        <div className="new-harvest-log-wrapper">
          <TierForm
            ref={tierFormRef}
            onHandleSubmit={handleSubmit}
            activeSub={activeSub}
          />
        </div>
      ),
      footer: (
        <div className="buttons-footer">
          <button
            type="button"
            className="btn equal-btns"
            onClick={() => {
              if (tierFormRef.current?.getHasUnsavedChanges()) {
                stepNavRef.current?.goToStep(UNSAVED_STEP);
                return;
              }
              handleCancel();
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form={TIER_FORM_ID}
            className="btn equal-btns"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create Tier"}
          </button>
        </div>
      ),
    },
    {
      content: (
        <div className="edit-inputs">
          <p className="modal-exit-confirm-msg">
            Are you sure you want to exit with unsaved changes?
          </p>
        </div>
      ),
      footer: (
        <div className="buttons-footer">
          <button
            type="button"
            className="btn equal-btns"
            onClick={() => stepNavRef.current?.goToStep(FORM_STEP)}
          >
            No
          </button>
          <button
            type="button"
            className="btn equal-btns"
            onClick={handleCancel}
          >
            Yes
          </button>
        </div>
      ),
    },
    {
      content: (
        <div className="edit-inputs modal-result-step">
          {outcome === "success" ? (
            <>
              <p className="modal-result-title">Success!</p>
              <p>New Tier has been created successfully!</p>
            </>
          ) : (
            <>
              <p className="modal-result-title">Could not create tier</p>
              <p>{errorMessage}</p>
            </>
          )}
        </div>
      ),
      footer: (
        <div className="buttons-footer">
          <button
            type="button"
            className="btn equal-btns"
            onClick={handleCloseFromResult}
          >
            Close
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <h3>
        {activeSub.name} <br />
        Create {isCombo && "Combo"} Tier
      </h3>
      <StepNavPanel ref={stepNavRef} steps={steps} className="modal-step-nav" />
    </>
  );
}
